import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button,
    Avatar,
    LinearProgress,
    Divider,
    Alert,
    CircularProgress,
    Tooltip,
    Badge,
    alpha
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    ShoppingCart as ShoppingCartIcon,
    LocalShipping as LocalShippingIcon,
    AttachMoney as AttachMoneyIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Print as PrintIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Warning as WarningIcon,
    Inventory as InventoryIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import axios from 'axios';

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        metrics: {
            totalRevenue: 0,
            totalOrders: 0,
            activeUsers: 0,
            pendingShipments: 0
        },
        recentOrders: [],
        pendingShipments: [],
        monthlyTrend: 0
    });
    const [refreshing, setRefreshing] = useState(false);
    const [apiErrors, setApiErrors] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setRefreshing(true);
            setApiErrors([]);

            console.log('🔍 Fetching dashboard data...');
            console.log('API URL:', process.env.REACT_APP_API_URL);
            console.log('Token exists:', !!localStorage.getItem('token'));

            // Fetch multiple data sources in parallel
            const [ordersResponse, usersResponse] = await Promise.allSettled([
                axios.get(`${process.env.REACT_APP_API_URL}/api/orders`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/user-history/users-overview`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                })
            ]);

            let orders = [];
            let users = [];
            const errors = [];

            // Handle orders response
            if (ordersResponse.status === 'fulfilled') {
                console.log('✅ Orders API Response:', ordersResponse.value.data);
                // Try multiple possible response structures
                orders = ordersResponse.value.data?.data?.data ||
                         ordersResponse.value.data?.data ||
                         ordersResponse.value.data?.orders ||
                         ordersResponse.value.data || [];
                console.log('📦 Orders parsed:', orders.length, 'orders found');
            } else {
                console.error('❌ Error fetching orders:', ordersResponse.reason);
                errors.push(`Orders API: ${ordersResponse.reason?.message || 'Failed to fetch'}`);
            }

            // Handle users response
            if (usersResponse.status === 'fulfilled') {
                console.log('✅ Users API Response:', usersResponse.value.data);
                users = usersResponse.value.data?.data || usersResponse.value.data || [];
                console.log('👥 Users parsed:', users.length, 'users found');
            } else {
                console.error('❌ Error fetching users:', usersResponse.reason);
                errors.push(`Users API: ${usersResponse.reason?.message || 'Failed to fetch'}`);
            }

            setApiErrors(errors);

            // Calculate metrics from real data
            const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
            const totalOrders = orders.length;
            const activeUsers = users.length;
            const pendingShipments = orders.filter(order =>
                order.status === 'confirmed' || order.status === 'processing'
            ).length;

            // Calculate monthly trend (basic calculation - compare with last 30 days)
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const recentOrders = orders.filter(order => new Date(order.createdAt) >= thirtyDaysAgo);
            const recentRevenue = recentOrders.reduce((sum, order) => sum + (order.total || 0), 0);
            const olderOrders = orders.filter(order => new Date(order.createdAt) < thirtyDaysAgo);
            const olderRevenue = olderOrders.reduce((sum, order) => sum + (order.total || 0), 0);

            let monthlyTrend = 0;
            if (olderRevenue > 0) {
                monthlyTrend = ((recentRevenue - olderRevenue) / olderRevenue) * 100;
            } else if (recentRevenue > 0) {
                monthlyTrend = 100; // 100% increase if no previous data
            }

            // Get recent orders (last 10)
            const sortedRecentOrders = orders
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 10);

            // Get orders needing shipment
            const ordersNeedingShipment = orders
                .filter(order => order.status === 'confirmed' || order.status === 'processing')
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

            console.log('Dashboard Data Calculated:', {
                totalRevenue,
                totalOrders,
                activeUsers,
                pendingShipments,
                monthlyTrend
            });

            setDashboardData({
                metrics: {
                    totalRevenue,
                    totalOrders,
                    activeUsers,
                    pendingShipments
                },
                recentOrders: sortedRecentOrders,
                pendingShipments: ordersNeedingShipment,
                monthlyTrend: Math.round(monthlyTrend * 10) / 10 // Round to 1 decimal
            });

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // Set some fallback data or show error message
            setDashboardData({
                metrics: {
                    totalRevenue: 0,
                    totalOrders: 0,
                    activeUsers: 0,
                    pendingShipments: 0
                },
                recentOrders: [],
                pendingShipments: [],
                monthlyTrend: 0
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'pending': '#f59e0b',
            'confirmed': '#3b82f6',
            'processing': '#8b5cf6',
            'shipped': '#10b981',
            'delivered': '#059669',
            'cancelled': '#ef4444'
        };
        return statusColors[status] || '#6b7280';
    };

    const getStatusLabel = (status) => {
        const statusLabels = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'processing': 'Processing',
            'shipped': 'Shipped',
            'delivered': 'Delivered',
            'cancelled': 'Cancelled'
        };
        return statusLabels[status] || status;
    };

    const getPriorityLevel = (daysOld) => {
        if (daysOld > 5) return { level: 'high', color: '#ef4444', label: 'Urgent' };
        if (daysOld > 2) return { level: 'medium', color: '#f59e0b', label: 'Medium' };
        return { level: 'low', color: '#10b981', label: 'Normal' };
    };

    const MetricCard = ({ title, value, icon, trend, color }) => (
        <Card
            sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha('#e2e8f0', 0.6)}`,
                borderRadius: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 30px ${alpha(color, 0.15)}`,
                    border: `1px solid ${alpha(color, 0.2)}`
                }
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: '#1a1a1a',
                                mb: 1,
                                fontSize: '2.2rem'
                            }}
                        >
                            {value}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: alpha('#1a1a1a', 0.6),
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                fontSize: '0.9rem'
                            }}
                        >
                            {title}
                        </Typography>
                        {trend && (
                            <Box display="flex" alignItems="center" mt={1}>
                                {trend > 0 ? (
                                    <TrendingUpIcon sx={{ color: '#10b981', fontSize: '1rem', mr: 0.5 }} />
                                ) : (
                                    <TrendingDownIcon sx={{ color: '#ef4444', fontSize: '1rem', mr: 0.5 }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: trend > 0 ? '#10b981' : '#ef4444',
                                        fontWeight: 600
                                    }}
                                >
                                    {Math.abs(trend)}% vs last month
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Avatar
                        sx={{
                            bgcolor: alpha(color, 0.1),
                            color: color,
                            width: 60,
                            height: 60,
                            border: `2px solid ${alpha(color, 0.2)}`
                        }}
                    >
                        {icon}
                    </Avatar>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress sx={{ color: '#ff5e17' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #334155 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1
                        }}
                    >
                        Dashboard Overview
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: alpha('#64748b', 0.8),
                            fontWeight: 500
                        }}
                    >
                        Monitor your business performance and manage operations
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={fetchDashboardData}
                    disabled={refreshing}
                    sx={{
                        borderColor: '#ff5e17',
                        color: '#ff5e17',
                        '&:hover': {
                            borderColor: '#ff5e17',
                            backgroundColor: alpha('#ff5e17', 0.05)
                        }
                    }}
                >
                    {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
            </Box>

            {/* API Error Messages */}
            {apiErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        API Connection Issues:
                    </Typography>
                    {apiErrors.map((error, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                            • {error}
                        </Typography>
                    ))}
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                        Check console for detailed error information.
                    </Typography>
                </Alert>
            )}

            {/* Metrics Cards */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Total Revenue"
                        value={`$${dashboardData.metrics.totalRevenue.toLocaleString()}`}
                        icon={<AttachMoneyIcon />}
                        trend={dashboardData.monthlyTrend}
                        color="#10b981"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Total Orders"
                        value={dashboardData.metrics.totalOrders.toLocaleString()}
                        icon={<ShoppingCartIcon />}
                        trend={8.2}
                        color="#3b82f6"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Active Users"
                        value={dashboardData.metrics.activeUsers.toLocaleString()}
                        icon={<PeopleIcon />}
                        trend={-2.1}
                        color="#8b5cf6"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Pending Shipments"
                        value={dashboardData.metrics.pendingShipments.toLocaleString()}
                        icon={<LocalShippingIcon />}
                        color="#f59e0b"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Recent Orders */}
                <Grid item xs={12} lg={8}>
                    <Paper
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha('#e2e8f0', 0.6)}`,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #ff5e17 0%, #667eea 50%, #011855 100%)',
                                opacity: 0.6
                            }
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}>
                                        <AssignmentIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                            Recent Orders
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8) }}>
                                            Latest customer orders and their status
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip
                                    label={`${dashboardData.recentOrders.length} orders`}
                                    sx={{
                                        bgcolor: alpha('#3b82f6', 0.1),
                                        color: '#3b82f6',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>

                            {dashboardData.recentOrders.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    No recent orders found
                                </Alert>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Order ID</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Customer</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dashboardData.recentOrders.map((order) => (
                                                <TableRow
                                                    key={order._id}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha('#ff5e17', 0.03)
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#3b82f6',
                                                                fontFamily: 'monospace'
                                                            }}
                                                        >
                                                            #{order._id.slice(-8).toUpperCase()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {order.user?.firstName} {order.user?.lastName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: alpha('#64748b', 0.8) }}>
                                                                {order.user?.email}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                                                            ${order.total?.toFixed(2)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={getStatusLabel(order.status)}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: alpha(getStatusColor(order.status), 0.1),
                                                                color: getStatusColor(order.status),
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8) }}>
                                                            {format(new Date(order.createdAt), 'MMM dd, yyyy')}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: alpha('#64748b', 0.6) }}>
                                                            {formatDistanceToNow(new Date(order.createdAt), { addSuffix: true })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" gap={1}>
                                                            <Tooltip title="View Details">
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{
                                                                        color: '#64748b',
                                                                        '&:hover': {
                                                                            color: '#ff5e17',
                                                                            backgroundColor: alpha('#ff5e17', 0.1)
                                                                        }
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Print Order">
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{
                                                                        color: '#64748b',
                                                                        '&:hover': {
                                                                            color: '#ff5e17',
                                                                            backgroundColor: alpha('#ff5e17', 0.1)
                                                                        }
                                                                    }}
                                                                >
                                                                    <PrintIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Orders Needing Shipment */}
                <Grid item xs={12} lg={4}>
                    <Paper
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha('#e2e8f0', 0.6)}`,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            height: 'fit-content',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)',
                                opacity: 0.8
                            }
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}>
                                        <LocalShippingIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                            Pending Shipments
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8) }}>
                                            Orders requiring shipment
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {dashboardData.pendingShipments.length === 0 ? (
                                <Alert
                                    severity="success"
                                    sx={{ borderRadius: 2 }}
                                    icon={<CheckCircleIcon />}
                                >
                                    All orders are shipped! 🎉
                                </Alert>
                            ) : (
                                <Box>
                                    {dashboardData.pendingShipments.slice(0, 8).map((order) => {
                                        const daysOld = Math.floor((new Date() - new Date(order.createdAt)) / (1000 * 60 * 60 * 24));
                                        const priority = getPriorityLevel(daysOld);

                                        return (
                                            <Card
                                                key={order._id}
                                                sx={{
                                                    mb: 2,
                                                    background: 'rgba(255,255,255,0.7)',
                                                    border: `1px solid ${alpha(priority.color, 0.2)}`,
                                                    borderRadius: 2,
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 4px 20px ${alpha(priority.color, 0.15)}`,
                                                        border: `1px solid ${alpha(priority.color, 0.4)}`
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: '#1a1a1a',
                                                                    fontFamily: 'monospace'
                                                                }}
                                                            >
                                                                #{order._id.slice(-8).toUpperCase()}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: alpha('#64748b', 0.8) }}>
                                                                {order.user?.firstName} {order.user?.lastName}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={priority.label}
                                                            size="small"
                                                            icon={
                                                                priority.level === 'high' ? <WarningIcon /> :
                                                                priority.level === 'medium' ? <ScheduleIcon /> :
                                                                <CheckCircleIcon />
                                                            }
                                                            sx={{
                                                                backgroundColor: alpha(priority.color, 0.1),
                                                                color: priority.color,
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                                            ${order.total?.toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: alpha('#64748b', 0.8) }}>
                                                            {daysOld} days old
                                                        </Typography>
                                                    </Box>

                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={order.status === 'confirmed' ? 25 : order.status === 'processing' ? 75 : 100}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            backgroundColor: alpha(priority.color, 0.1),
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: priority.color,
                                                                borderRadius: 3
                                                            }
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: priority.color,
                                                            fontWeight: 600,
                                                            mt: 1,
                                                            display: 'block'
                                                        }}
                                                    >
                                                        {getStatusLabel(order.status)}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}

                                    {dashboardData.pendingShipments.length > 8 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: alpha('#64748b', 0.8),
                                                textAlign: 'center',
                                                display: 'block',
                                                mt: 2
                                            }}
                                        >
                                            +{dashboardData.pendingShipments.length - 8} more orders pending
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminDashboard;